import {
  Tooltip,
  TooltipProps,
  useEventCallback,
  useTimeout,
} from "@fluentui/react-components";
import React, {
  FocusEvent,
  HTMLAttributes,
  MouseEvent,
  PointerEvent,
  ReactElement,
  cloneElement,
  useEffect,
  useState,
} from "react";

const SHOW_DELAY_MS = 1000;

type AppSideNavNameTooltipProps = Omit<
  TooltipProps,
  "children" | "showDelay" | "visible" | "onVisibleChange"
> & {
  children: ReactElement<HTMLAttributes<HTMLElement>>;
  disabled?: boolean;
};

export const AppSideNavNameTooltip = ({
  children,
  disabled = false,
  ...props
}: AppSideNavNameTooltipProps) => {
  const [visible, setVisible] = useState(false);
  const [setDelayTimeout, clearDelayTimeout] = useTimeout();
  const onVisibleChange = useEventCallback((_, data: { visible: boolean }) => {
    clearDelayTimeout();
    if (data.visible && !disabled) {
      // 別の tooltip が表示中だと Fluent UI の showDelay が効かないため、表示を自前で遅らせる
      setDelayTimeout(() => setVisible(true), SHOW_DELAY_MS);
    } else {
      setVisible(false);
    }
  });

  useEffect(() => {
    if (disabled) {
      clearDelayTimeout();
      setVisible(false);
    }
  }, [disabled, clearDelayTimeout]);

  // 名称が未取得のときは空の tooltip を付けない
  if (!props.content) return children;

  return (
    <Tooltip
      {...props}
      showDelay={0}
      visible={visible && !disabled}
      onVisibleChange={onVisibleChange}
    >
      {/* 非表示の間は Fluent UI が onVisibleChange(false) を呼ばないため、離脱・クリック時は自前で予約を取り消す */}
      {cloneElement(children, {
        onPointerLeave: (event: PointerEvent<HTMLElement>) => {
          clearDelayTimeout();
          children.props.onPointerLeave?.(event);
        },
        onBlur: (event: FocusEvent<HTMLElement>) => {
          clearDelayTimeout();
          children.props.onBlur?.(event);
        },
        onClick: (event: MouseEvent<HTMLElement>) => {
          clearDelayTimeout();
          setVisible(false);
          children.props.onClick?.(event);
        },
      })}
    </Tooltip>
  );
};
